import React from 'react';
import { Link } from 'react-router-dom';
import LandingNav from './LandingNav';
import Footer from './Footer';

const About = () => {
  const mission = [
    {
      icon: '🎯',
      title: 'Our Mission',
      description: 'To make Web3 security accessible to everyone by providing enterprise-grade protection for crypto assets through innovative AI-powered solutions.'
    },
    {
      icon: '👁️',
      title: 'Our Vision',
      description: 'A world where every crypto user has access to military-grade security, making digital asset protection as simple as connecting a wallet.'
    },
    {
      icon: '💡',
      title: 'Our Values',
      description: 'Privacy-first, decentralized, and user-controlled. We believe your keys should always remain yours, with zero-knowledge architecture.'
    }
  ];

  const stats = [
    { number: '$2.4B+', label: 'Assets Protected' },
    { number: '50K+', label: 'Active Wallets' },
    { number: '100+', label: 'Supported Chains' },
    { number: '99.9%', label: 'Uptime' }
  ];

  const team = [
    {
      role: 'Security',
      description: 'Former security experts from leading blockchain companies',
      icon: '🔐'
    },
    {
      role: 'AI/ML',
      description: 'Machine learning specialists with decades of combined experience',
      icon: '🤖'
    },
    {
      role: 'Blockchain',
      description: 'Core contributors to major blockchain protocols',
      icon: '⛓️'
    },
    {
      role: 'Product',
      description: 'UX designers focused on making Web3 security simple',
      icon: '🎨'
    }
  ];

  const milestones = [
    {
      year: '2023',
      title: 'Platform Launch',
      description: 'Launched AureumVault with support for major wallets and chains'
    },
    {
      year: '2024',
      title: 'AI Integration',
      description: 'Introduced AI-powered threat detection and predictive security'
    },
    {
      year: '2024',
      title: '$100M Insurance',
      description: 'Secured comprehensive insurance coverage for all users'
    },
    {
      year: '2025',
      title: '50K+ Users',
      description: 'Reached 50,000 active wallets protecting $2.4B+ in assets'
    }
  ];

  return (
    <div className="min-h-screen bg-deepspace">
      <LandingNav />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-deepspace via-deepspace-100 to-deepspace-200"></div>
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyber-blue/10 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyber-green/10 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-text-primary mb-6">
                Securing the Future of
                <span className="block bg-gradient-to-r from-cyber-blue to-cyber-green bg-clip-text text-transparent">
                  Digital Assets
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-text-secondary leading-relaxed mb-8">
                AureumVault is a Web3-native security platform built by blockchain experts, security professionals, and AI specialists to protect your crypto assets.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/auth" className="px-8 py-4 bg-gradient-to-r from-cyber-blue to-cyber-green text-white font-bold rounded-xl hover:scale-105 transition-transform duration-300 inline-block text-center">
                  Connect Wallet
                </Link>
                <Link to="/security" className="px-8 py-4 border border-cyber-blue/50 text-cyber-blue font-bold rounded-xl backdrop-blur-sm hover:bg-cyber-blue/10 transition-all duration-300 inline-block text-center">
                  View Security Audit
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyber-blue/20 to-cyber-green/20 rounded-3xl blur-2xl"></div>
              <div className="relative bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-3xl p-8">
                <img src="/images/all/cross-chain-bridge.png" alt="About" className="w-full h-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-deepspace-400">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mission.map((item, index) => (
              <div key={index} className="bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-2xl p-8 text-center hover:border-cyber-blue/50 transition-all duration-300 hover:scale-105 shadow-[0_8px_30px_rgb(0,0,0,0.5)]">
                <div className="text-5xl mb-6">{item.icon}</div>
                <h3 className="text-2xl font-bold text-text-primary mb-4">{item.title}</h3>
                <p className="text-text-secondary leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-2xl p-8 hover:border-cyber-blue/50 transition-all duration-300">
                  <div className="text-4xl font-bold text-cyber-blue mb-2">{stat.number}</div>
                  <div className="text-sm text-text-muted">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-deepspace-400">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Built by Experts
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Our team combines expertise in blockchain security, AI, and Web3 development.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-2xl p-8 text-center hover:border-cyber-green/50 transition-all duration-300">
                <div className="text-5xl mb-4">{member.icon}</div>
                <h3 className="text-xl font-bold text-text-primary mb-3">{member.role}</h3>
                <p className="text-sm text-text-secondary leading-relaxed">{member.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Our Journey
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Key milestones in building the most secure Web3 platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="relative">
                <div className="bg-dark-card/50 backdrop-blur-sm border border-dark-border/50 rounded-2xl p-8 hover:border-cyber-blue/50 transition-all duration-300 shadow-[0_8px_30px_rgb(0,0,0,0.5)]">
                  <div className="text-3xl font-bold text-cyber-blue mb-3">{milestone.year}</div>
                  <h3 className="text-xl font-bold text-text-primary mb-3">{milestone.title}</h3>
                  <p className="text-sm text-text-secondary leading-relaxed">{milestone.description}</p>
                </div>
                {index < milestones.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-cyber-blue/30"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-deepspace-400">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-cyber-blue/10 via-cyber-green/10 to-cyber-purple/10 backdrop-blur-sm border border-cyber-blue/30 rounded-3xl p-12 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
              Join the AureumVault Community
            </h2>
            <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
              Be part of the future of Web3 security. Protect your assets today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth" className="px-8 py-4 bg-gradient-to-r from-cyber-blue to-cyber-green text-white font-bold rounded-xl hover:scale-105 transition-transform duration-300 inline-block text-center">
                Connect Wallet
              </Link>
              <Link to="/auth" className="px-8 py-4 border border-cyber-green/50 text-cyber-green font-bold rounded-xl backdrop-blur-sm hover:bg-cyber-green/10 transition-all duration-300 inline-block text-center">
                Create Wallet
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;